/**
 * Allow-list gate for forwarding the Platinum bearer token (`platinum_token`)
 * to an OIDC `/oauth/authorize` URL.
 *
 * Why this is narrower than `isValidAbsoluteRedirectUrl`:
 * `isValidAbsoluteRedirectUrl` accepts `/oauth/authorize` on any host because
 * Helium enforces its own `redirect_uri` allow-list against the inner
 * `redirect_uri` parameter. That protection only covers the OIDC redirect
 * chain. It does NOT cover `platinum_token`, which we append to the OUTER
 * URL before navigation. Without a host gate, an attacker-supplied
 * `redirectUrl` of the form `https://attacker.com/oauth/authorize?...` would
 * receive the bearer token in its query string.
 *
 * Trusted hosts (matches the issuer shapes called out in the broader
 * `isValidAbsoluteRedirectUrl` comment):
 * - `helium.<env>.openpath.com`
 * - `helium.<region>.alta.avigilon.com`
 * - `api.<region>.alta.avigilon.com`
 * - `api.openpath.com`
 * - `localhost` and `127.0.0.1` (loopback) for local dev.
 *
 * Both `http:` and `https:` are accepted on the trusted issuer hosts because
 * the dev Helium environment is served over plain http
 * (`http://helium.dev.openpath.com`). The host allow-list -- not the
 * protocol -- is what prevents bearer-token leakage to attacker-controlled
 * origins.
 */
const ISSUER_HOSTS: RegExp[] = [
  /^helium\.[a-z0-9-]+\.openpath\.com$/,
  /^helium\.[a-z0-9-]+\.alta\.avigilon\.com$/,
  /^api\.[a-z0-9-]+\.alta\.avigilon\.com$/,
  /^api\.openpath\.com$/,
];

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1']);

// Identity-scoped authorize path, or the org-scoped equivalent
// (/orgs/{orgId}/oauth/authorize) with a numeric orgId.
const AUTHORIZE_PATH_RE = /^\/(orgs\/\d+\/)?oauth\/authorize$/;

export const isTrustedOidcIssuer = (redirectUrl?: string): boolean => {
  if (!redirectUrl) return false;

  let parsed: URL;
  try {
    parsed = new URL(redirectUrl);
  } catch {
    return false;
  }

  if (!AUTHORIZE_PATH_RE.test(parsed.pathname)) return false;

  if (parsed.protocol === 'http:' && LOOPBACK_HOSTS.has(parsed.hostname)) {
    return true;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;

  return ISSUER_HOSTS.some((re) => re.test(parsed.hostname));
};
